import React from 'react'
import { Checkbox, Grid, TextInput } from '@mantine/core'
import { IconLayoutGrid, IconMapSearch } from '@tabler/icons-react'
import * as AppStore from './../state/app-store'
import { useIsMobile } from '../lib/useIsMobile'
import { convertMetricToImperial } from '../lib/helper'
import * as Selectors from './../state/selectors'
import * as Types from '../declarations'
import CountryFilterInput from './CountryFilterInput'
// import ContinentFilterInput from './ContinentFilterInput'
import TimeOfYearInput from './TimeOfYearInput'
import BudgetInput from './BudgetInput'
import RadioButtonGroup from './RadioButtonGroup'
import MultiSelectButtonGroup from './MultiSelectButtonGroup'
import SearchControl from './SearchControl'
import { getVersion } from '../lib/withVersion'

interface Props {
	currentSearchFilter: Types.SearchFilter
	mapMode: boolean
	treks: Types.App.HomePageTrip[]
	onChange: (searchFilter: Types.SearchFilter) => void
	setMapMode: (mapMode: boolean) => void
}

const SearchFilter = ({
	mapMode,
	currentSearchFilter,
	treks,
	onChange,
	setMapMode,
}: Props) => {
	const version = getVersion()

	const [searchValue, setSearchValue] = React.useState(currentSearchFilter.text)
	const [timeOfYear, setTimeOfYear] = React.useState(
		currentSearchFilter.timeOfYear,
	)
	const [countryFilters, setCountryFilters] = React.useState<string[]>(
		currentSearchFilter.countryFilters,
	)
	const [continentFilters] = React.useState<string[]>(
		currentSearchFilter.continentFilters,
	)
	const [mapBounds, setMapBounds] = React.useState<Types.MapBounds | undefined>(
		currentSearchFilter.mapBounds,
	)
	const [duration, setDuration] = React.useState<string | undefined>(undefined)
	const [heightFilter, setHeightFilter] = React.useState<number | undefined>(
		undefined,
	)
	const [routeTypeFilter] = React.useState<'TREK' | 'CLIMB' | undefined>(
		undefined,
	)

	const [isWithoutPermit, setIsWithoutPermit] = React.useState<boolean>(false)

	const mostExpensiveTrip: number | undefined = (() => {
		const tempMostExpensiveTrip = (treks ?? []).reduce(
			(
				maxItem: Types.App.HomePageTrip | undefined,
				currentItem: Types.App.HomePageTrip,
			) => {
				if (
					currentItem?.lowestCost &&
					+currentItem.lowestCost >
						(maxItem?.lowestCost ? +maxItem?.lowestCost : 0)
				) {
					return currentItem
				} else {
					return maxItem
				}
			},
			undefined,
		)?.lowestCost

		return tempMostExpensiveTrip !== undefined
			? +tempMostExpensiveTrip
			: undefined
	})()

	const [budgetFilter, setBudgetFilter] = React.useState<number | undefined>(
		mostExpensiveTrip,
	)

	const [simpleDifficultyFilter, setSimpleDifficultyFilter] = React.useState<
		number[] | undefined
	>(undefined)

	const [
		trekkingDifficultyFilter,
		setTrekkingDifficultyFilter,
	] = React.useState<number[] | undefined>(undefined)

	const isMobile = useIsMobile()

	React.useEffect(() => {
		// override local state values if need be
		if (
			JSON.stringify(mapBounds) !==
			JSON.stringify(currentSearchFilter.mapBounds)
		) {
			setMapBounds(currentSearchFilter.mapBounds)
		}
	}, [currentSearchFilter.mapBounds])

	React.useEffect(() => {
		const raiseOnChangeEvent = {
			// ...currentSearchFilter, // for things outwith this component (eg mapBounds)
			text: searchValue,
			timeOfYear,
			countryFilters,
			continentFilters,
			mapBounds,
			duration,
			heightFilter,
			isWithoutPermit,
			routeTypeFilter,
			budgetFilter:
				budgetFilter === mostExpensiveTrip ? undefined : budgetFilter,
			simpleDifficultyFilter,
			trekkingDifficultyFilter,
		}
		onChange(raiseOnChangeEvent)
	}, [
		searchValue,
		timeOfYear,
		countryFilters,
		continentFilters,
		// mapBounds,
		duration,
		heightFilter,
		isWithoutPermit,
		routeTypeFilter,
		budgetFilter,
		simpleDifficultyFilter,
		trekkingDifficultyFilter,
	])

	const durationOptions = [
		{ display: '1 wk', value: '1 wk' },
		{ display: '2 wk', value: '2 wk' },
		{ display: '3 wk', value: '3 wk' },
		{ display: '4 wk', value: '4 wk' },
		{ display: '4+ wk', value: '4+ wk' },
	]

	const unit = AppStore.useAppSelector(Selectors.unitSelector)

	const resultModeOptions = [
		{
			display: (
				<>
					<IconMapSearch size={18} />
					&nbsp;Map&nbsp;
				</>
			),
			value: true,
		},
		{
			display: (
				<>
					<IconLayoutGrid size={18} />
					&nbsp;Results
				</>
			),
			value: false,
		},
	]

	const metricHeightOptions = [
		{ display: '6000', value: 6000 },
		{ display: '6500', value: 6500 },
		{ display: '7000', value: 7000 },
		{ display: '7500', value: 7500 },
		{ display: '8000', value: 8000 },
	]

	const heightOptions = (unit === 'metric'
		? metricHeightOptions
		: metricHeightOptions.map(({ value }) => ({
				display: convertMetricToImperial(value),
				value,
		  }))
	).map((option) => ({
		...option,
		display: option.display + (unit === 'metric' ? ' m' : ' ft'),
	}))

	const isTrekking = version === Types.VERSION.TREKKING

	const countryCodes = React.useMemo(() => {
		const arrayOfCountries = treks.map((trek) => trek.countries ?? [])
		const flattenedCountries = arrayOfCountries.flat(1)

		const uniqueCountries: string[] = Array.from(new Set(flattenedCountries))
		return uniqueCountries
	}, [treks])

	return (
		<>
			<div id="search-form">
				<div id="search-filter-section">
					<div className="search-filter-line">
						<Grid>
							<Grid.Col span={{ base: 12, sm: 6, md: 5 }}>
								<TextInput
									size={'md'}
									placeholder={`filter ${
										version === Types.VERSION.TREKKING ? 'treks' : 'mountains'
									} by name...`}
									onChange={(e) => setSearchValue(e.target.value)}
								/>
							</Grid.Col>
							{/* <Grid.Col span={{base: 12, sm: 4, md: 3}}>
								<ContinentFilterInput
									treks={treks}
									onChange={setContinentFilters}
								/>
							</Grid.Col> */}

							<Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
								<CountryFilterInput
									countryCodes={countryCodes}
									onChange={setCountryFilters}
								/>
							</Grid.Col>

							{/* <div className="hide-medium"> */}
							<Grid.Col
								className="hide-map-breakpoint"
								span={{ base: 12, md: 3, lg: 3 }}
							>
								<SearchControl
									// label={<></>}
									control={
										<RadioButtonGroup<boolean>
											items={resultModeOptions}
											value={mapMode}
											size="md"
											onChange={(val) => {
												setMapMode(val as boolean)
												if (!val) {
													setMapBounds(undefined)
												}
											}}
											nullable={false}
										/>
									}
								/>
							</Grid.Col>
							{/* </div> */}
						</Grid>
					</div>

					{!isMobile && (
						<>
							{isTrekking && (
								<>
									{/* <div className="search-filter-line"> */}
									<Grid>
										<Grid.Col span={{ base: 12, sm: 10, md: 10 }}>
											<TimeOfYearInput onChange={setTimeOfYear} />
										</Grid.Col>

										<Grid.Col span={{ base: 12, sm: 2, md: 2 }}>
											<SearchControl
												label={
													<>
														{/* <License size={rem(14)} /> */}
														Permits
													</>
												}
												control={
													<>
														<div
															style={{
																verticalAlign: 'middle',
																display: 'inline-block',
															}}
														>
															<Checkbox
																label="Not needed"
																checked={isWithoutPermit}
																onChange={(e) =>
																	setIsWithoutPermit(e.target.checked)
																}
																// color="yellow"
																size="md"
															/>
														</div>
													</>
												}
											/>
										</Grid.Col>
										{/* </Grid>
							</div>

							<div className="search-filter-line">
								<Grid> */}
										<Grid.Col span={{ base: 12, sm: 5, md: 4 }}>
											<SearchControl
												label={
													<>
														{/* <PhotoPlus size={rem(14)} /> */}
														Duration
													</>
												}
												control={
													<RadioButtonGroup<string>
														items={durationOptions}
														value={duration}
														onChange={setDuration}
														size="xs"
													/>
												}
											/>
										</Grid.Col>

										<Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
											<SearchControl
												label={
													<>
														{/* <Stretching size={rem(14)} /> */}
														Difficulty
													</>
												}
												control={
													<MultiSelectButtonGroup
														items={[
															{
																display: 'Easy',
																value: 0,
															},
															{
																display: 'Moderate',
																value: 1,
															},
															{
																display: 'Challenging',
																value: 2,
															},
														]}
														onChange={(ev) => {
															setTrekkingDifficultyFilter(ev as number[])
														}}
														size="xs"
													/>
												}
											/>
										</Grid.Col>

										<Grid.Col span={{ base: 12, sm: 3, md: 4 }}>
											{!!mostExpensiveTrip && (
												<SearchControl
													label={
														<>
															{/* <CashBanknote size={rem(14)} /> */}
															Budget
														</>
													}
													control={
														<BudgetInput
															value={budgetFilter}
															upperValue={mostExpensiveTrip}
															onChange={setBudgetFilter}
														/>
													}
												/>
											)}
										</Grid.Col>
									</Grid>
									{/* </div> */}
								</>
							)}

							{!isTrekking && (
								<>
									{/* <div className="search-filter-line"> */}
									<Grid>
										<Grid.Col span={{ base: 12, sm: 10, md: 7 }}>
											<TimeOfYearInput onChange={setTimeOfYear} />
										</Grid.Col>

										<Grid.Col span={{ base: 12, sm: 2, md: 2 }}>
											<SearchControl
												label={
													<>
														{/* <License size={rem(14)} /> */}
														Permits
													</>
												}
												control={
													<>
														<div
															style={{
																verticalAlign: 'middle',
																display: 'inline-block',
															}}
														>
															<Checkbox
																label="Not needed"
																checked={isWithoutPermit}
																onChange={(e) =>
																	setIsWithoutPermit(e.target.checked)
																}
																// color="yellow"
																size="md"
															/>
														</div>
													</>
												}
											/>
										</Grid.Col>
										{/* </Grid>
							</div>

							<div className="search-filter-line">
								<Grid> */}

										<Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
											{!!mostExpensiveTrip && (
												<SearchControl
													label={
														<>
															{/* <CashBanknote size={rem(14)} /> */}
															Budget
														</>
													}
													control={
														<BudgetInput
															value={budgetFilter}
															upperValue={mostExpensiveTrip}
															onChange={setBudgetFilter}
														/>
													}
												/>
											)}
										</Grid.Col>

										{version === Types.VERSION.PEAKS && (
											<Grid.Col span={{ base: 12, sm: 6, md: 5 }}>
												<SearchControl
													label={
														<>
															{/* <Mountain size={rem(14)} /> */}
															Higher than
														</>
													}
													control={
														<RadioButtonGroup<number>
															items={heightOptions}
															value={heightFilter}
															onChange={setHeightFilter}
															size="xs"
														/>
													}
												/>
											</Grid.Col>
										)}

										<Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
											<SearchControl
												label={
													<>
														{/* <PhotoPlus size={rem(14)} /> */}
														Duration
													</>
												}
												control={
													<RadioButtonGroup<string>
														items={durationOptions}
														value={duration}
														onChange={setDuration}
														size="xs"
													/>
												}
											/>
										</Grid.Col>

										<Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
											<SearchControl
												label={
													<>
														{/* <Stretching size={rem(14)} /> */}
														Difficulty
													</>
												}
												control={
													<MultiSelectButtonGroup<number>
														items={[
															{
																display: 'F',
																value: 1,
															},
															{
																display: 'PD',
																value: 2,
															},
															{
																display: 'AD',
																value: 3,
															},
															{
																display: 'D',
																value: 4,
															},
															{
																display: 'TD',
																value: 5,
															},
															{
																display: 'ED',
																value: 6,
															},
															// {
															// 	display: 'ABO',
															// 	value: 7,
															// },
														]}
														onChange={(ev) => {
															setSimpleDifficultyFilter(ev as number[])
														}}
														size="xs"
													/>
												}
											/>
										</Grid.Col>
									</Grid>
									{/* </div> */}
								</>
							)}
						</>
					)}
				</div>
			</div>
		</>
	)
}

export default SearchFilter
